import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { getConsent, saveConsent, applyConsent, CONSENT_EVENT } from "@/lib/consent";
import { CookieSettingsDialog } from "./CookieSettingsDialog";

const BTN = "h-10 px-5 rounded-full text-sm font-semibold transition-colors";
const NADA = { preferencias: false, marketing: false, medicion: false };

// Con un modal abierto (Radix bloquea el resto de la página) el aviso se oculta y vuelve al cerrarlo.
function useModalAbierto() {
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    const mirar = () => setAbierto(document.body.style.pointerEvents === "none");
    const obs = new MutationObserver(mirar);
    obs.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    mirar();
    return () => obs.disconnect();
  }, []);
  return abierto;
}

export function CookieBanner() {
  const [consent, setConsent] = useState(getConsent);
  const [ajustes, setAjustes] = useState(false);
  const modal = useModalAbierto();

  useEffect(() => { applyConsent(getConsent()); }, []);
  useEffect(() => {
    const abrir = () => setAjustes(true);
    window.addEventListener(CONSENT_EVENT, abrir);
    return () => window.removeEventListener(CONSENT_EVENT, abrir);
  }, []);

  const guardar = (prefs) => { setConsent(saveConsent(prefs)); setAjustes(false); };
  const todo = (v) => guardar({ preferencias: v, marketing: v, medicion: v });

  return (
    <>
      {!consent && !modal && (
        <div role="dialog" aria-label="Aviso de cookies" data-testid="cookie-banner"
          className="fixed z-[70] bottom-3 left-3 right-3 sm:left-5 sm:right-auto sm:bottom-5 sm:max-w-xl rounded-3xl border-2 border-[#0A2540] bg-[#ECFDF5] shadow-2xl shadow-[#0A2540]/20 p-5 animate-in fade-in-0 slide-in-from-bottom-4 duration-300">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 shrink-0 rounded-2xl bg-white border border-[#0A2540]/15 flex items-center justify-center"><Cookie className="w-5 h-5 text-emerald-600" /></div>
            <div className="min-w-0">
              <p className="font-heading font-extrabold text-[#0A2540]">Tu privacidad es importante</p>
              <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
                Usamos cookies propias y de terceros para que la web funcione, recordar tus preferencias, ofrecerte el chat de soporte y medir el uso. Puedes aceptarlas, rechazarlas o configurarlas.{" "}
                <Link to="/legal/cookies" className="font-semibold text-emerald-700 underline" data-testid="cookie-banner-policy-link">Política de cookies</Link> ·{" "}
                <Link to="/legal/mas-informacion" className="font-semibold text-emerald-700 underline" data-testid="cookie-banner-info-link">Más información</Link>
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button onClick={() => setAjustes(true)} className={`${BTN} border-2 border-[#0A2540] text-[#0A2540] hover:bg-white`} data-testid="cookie-banner-settings">Configuración</button>
            <button onClick={() => todo(false)} className={`${BTN} bg-[#0A2540] text-white hover:bg-[#031B33]`} data-testid="cookie-banner-reject">Rechazar todo</button>
            <button onClick={() => todo(true)} className={`${BTN} bg-emerald-500 text-white hover:bg-emerald-600`} data-testid="cookie-banner-accept">Aceptar todo</button>
          </div>
        </div>
      )}
      <CookieSettingsDialog open={ajustes} onOpenChange={setAjustes} initial={consent || NADA} onSave={guardar} />
    </>
  );
}
